import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query'
import { GantnerDevicesApi } from '@trybeapp/sdk.customer'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { GantnerApi } from '@trybeapp/sdk'
import { apiConfiguration } from 'utilities/TrybeApiClient'
import { LegacySdkResponseError } from 'api/api.types'
import { toIsoDate } from 'utilities/DateUtils'

const api = new GantnerDevicesApi()
const tsApi = new GantnerApi(apiConfiguration)

export const useGantnerDevices = (params = {}) => {
  const siteId = useCurrentSiteId()

  return useQuery<any, LegacySdkResponseError>(
    ['gantnerDevices', { siteId, ...params }],
    () => api.listGantnerDevices(siteId, params),
    {
      staleTime: Infinity,
    }
  )
}

export const useCreateGantnerDevice = () => {
  const queryClient = useQueryClient()

  return useMutation<any, LegacySdkResponseError, [string, any]>(
    ([siteId, data]) => api.createGantnerDevice(siteId, data),
    {
      onSuccess: (e, [siteId]) => {
        queryClient.invalidateQueries(['gantnerDevices', { siteId }])
      },
    }
  )
}

export const useGantnerDevice = (siteId: string, deviceId: string, options: UseQueryOptions = {}) =>
  useQuery<any, LegacySdkResponseError>(
    ['gantnerDevice', { deviceId }],
    () => api.getGantnerDevice(siteId, deviceId),
    options
  )

export const useUpdateGantnerDevice = (siteId: string, deviceId: string) => {
  const queryClient = useQueryClient()

  return useMutation<any, LegacySdkResponseError, any>(
    (vals) => api.updateGantnerDevice(siteId, deviceId, vals),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['gantnerDevices', { siteId }])
        queryClient.invalidateQueries(['gantnerDevice', { deviceId }])
      },
    }
  )
}

export const useDeleteGantnerDevice = () => {
  const queryClient = useQueryClient()

  return useMutation<any, LegacySdkResponseError, [string, string]>(
    ([siteId, deviceId]) => api.deleteGantnerDevice(siteId, deviceId),
    {
      onSuccess: (e, [siteId, deviceId]) => {
        queryClient.invalidateQueries(['gantnerDevice', { deviceId }])
        queryClient.invalidateQueries(['gantnerDevices', { siteId }])
      },
    }
  )
}

export const useLogs = (params) => {
  const siteId = useCurrentSiteId()

  if (params.dateFrom) {
    params.dateFrom = toIsoDate(params.dateFrom)
  }

  if (params.dateTo) {
    params.dateTo = toIsoDate(params.dateTo)
  }

  return useQuery(['gantnerLogs', { siteId }], () => api.listGantnerDeviceLogs(siteId, params))
}

export const useInfiniteGantnerDevices = (params: Record<string, any> = {}) => {
  const siteId = useCurrentSiteId()
  params.siteId = params.siteId ?? siteId

  return useInfiniteQuery(['gantnerDevices', { params }], fetchGantnerDevices, {
    getNextPageParam: (lastPage: any, pages) => {
      return lastPage.nextPage
    },
  })
}

const fetchGantnerDevices = async ({ queryKey, pageParam: page = 1 }) => {
  const { params } = queryKey[1]

  const response = await api.listGantnerDevices(params.siteId, params)

  return {
    data: response.data,
    page,
    nextPage: response.meta.last_page > page ? page + 1 : undefined,
    total: response.meta.total,
  }
}

export const useOpenGantnerDevice = () => {
  return useMutation(([siteId, deviceId]: [string, string]) => {
    return tsApi.actionOpenGantnerGate({
      siteId,
      deviceId,
    })
  })
}
